import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, X, History } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import type { AlertSeverity } from "@/data/alertsEngine";
import { generateUnifiedAlerts } from "@/data/alertFeed";
import { useSafetyControl } from "@/data/SafetyControlContext";
import { cn } from "@/lib/utils";

const SEVERITY_STYLES: Record<AlertSeverity, string> = {
  critical: "text-destructive bg-destructive/10 border-destructive/20",
  warning: "text-warning bg-warning/10 border-warning/20",
  info: "text-muted-foreground bg-muted/30 border-border",
};

const SEVERITY_DOT: Record<AlertSeverity, string> = {
  critical: "bg-destructive",
  warning: "bg-warning",
  info: "bg-muted-foreground/40",
};

export function AlertNotificationCenter() {
  const { jobs, issues, contractors, opportunities, credits, quotes } = useAppData();
  const { rules } = useSafetyControl();
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "critical" | "warning">("all");

  const alerts = useMemo(
    () => generateUnifiedAlerts(jobs, issues, contractors, opportunities, credits, quotes, rules),
    [jobs, issues, contractors, opportunities, credits, quotes, rules]
  );

  const badgeAlerts = useMemo(
    () => alerts.filter((a) => a.severity !== "info"),
    [alerts]
  );
  const unreadBadgeCount = badgeAlerts.filter((a) => !readIds.has(a.id)).length;

  const filteredAlerts = useMemo(() => {
    if (filter === "all") return badgeAlerts;
    return badgeAlerts.filter((a) => a.severity === filter);
  }, [badgeAlerts, filter]);

  const markRead = useCallback((id: string) => {
    setReadIds((prev) => new Set(prev).add(id));
  }, []);

  const markAllRead = useCallback(() => {
    setReadIds(new Set(badgeAlerts.map((a) => a.id)));
  }, [badgeAlerts]);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-md hover:bg-muted transition-colors"
        aria-label="Alerts"
      >
        <Bell className={cn("h-4.5 w-4.5", unreadBadgeCount > 0 ? "text-foreground" : "text-muted-foreground")} />
        {unreadBadgeCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center leading-none">
            {unreadBadgeCount > 99 ? "99+" : unreadBadgeCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          <div className="fixed right-2 top-12 sm:absolute sm:right-0 sm:top-full sm:mt-1.5 z-50 w-[calc(100vw-16px)] sm:w-[380px] max-h-[75vh] rounded-xl border bg-card shadow-xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                <h3 className="text-xs font-bold text-foreground">Alerts</h3>
                {unreadBadgeCount > 0 && (
                  <span className="text-[9px] font-bold text-destructive bg-destructive/10 border border-destructive/20 rounded-full px-1.5 py-0.5 leading-none">
                    {unreadBadgeCount} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadBadgeCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[10px] text-primary hover:underline font-medium"
                  >
                    Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-muted">
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Filter strip */}
            <div className="flex gap-1.5 px-4 py-2 border-b">
              {(["all", "critical", "warning"] as const).map((f) => {
                const count = f === "all" ? badgeAlerts.length : badgeAlerts.filter((a) => a.severity === f).length;
                return (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={cn(
                      "rounded-md border px-2.5 py-1 text-[10px] font-semibold transition-colors capitalize",
                      filter === f
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground hover:bg-muted border-border"
                    )}
                  >
                    {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)} ({count})
                  </button>
                );
              })}
            </div>

            {/* Alert list */}
            <div className="flex-1 overflow-y-auto">
              {filteredAlerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                  <Bell className="h-6 w-6 mb-2 opacity-30" />
                  <p className="text-xs">No alerts to display</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredAlerts.slice(0, 15).map((alert) => {
                    const isUnread = !readIds.has(alert.id);
                    return (
                      <Link
                        key={alert.id}
                        to={alert.actionLink}
                        onClick={() => {
                          markRead(alert.id);
                          setOpen(false);
                        }}
                        className={cn(
                          "flex gap-3 px-4 py-3 hover:bg-muted/40 transition-colors",
                          isUnread && "bg-primary/[0.03]"
                        )}
                      >
                        <div className="pt-1.5 shrink-0">
                          <div className={cn("h-2 w-2 rounded-full", SEVERITY_DOT[alert.severity], !isUnread && "opacity-40")} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-1.5 flex-wrap">
                            <span className={cn(
                              "text-[11px] font-semibold leading-snug break-words",
                              isUnread ? "text-foreground" : "text-muted-foreground"
                            )}>
                              {alert.title}
                            </span>
                            <span className={cn(
                              "inline-flex items-center shrink-0 rounded border px-1 py-0 text-[8px] font-bold uppercase tracking-wider",
                              SEVERITY_STYLES[alert.severity]
                            )}>
                              {alert.severity}
                            </span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed break-words">
                            {alert.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[9px] text-primary font-medium whitespace-nowrap">{alert.actionLabel} →</span>
                            <span className="text-[9px] text-muted-foreground/50 capitalize">{alert.category}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer — View All */}
            <div className="border-t px-4 py-2.5 bg-muted/20">
              <Link
                to="/alerts"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-primary hover:underline"
              >
                <History className="h-3 w-3" />
                View All Alerts
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
